import React, { SetStateAction, useState } from 'react';
import { Button, Col, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { FaTh, FaThList } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import { IAllStates } from '../../../../store/rootReducer';
import { IMyCampaignsFilterProps } from '../dto/IMyCampaigns';
import { SearchInput } from '../../../../shared/components/SearchInput';

interface ICampaignFilterProps {
    filterParams: IMyCampaignsFilterProps;
    setFilterParams: React.Dispatch<SetStateAction<IMyCampaignsFilterProps>>;
    setIsCardView?: React.Dispatch<SetStateAction<boolean>>;
    isCardView?: boolean;
    isDeclinedStatus?: boolean;
    showCampaignStatus?: boolean;
    searchKey?: string;
    showLongButton?: boolean;
    showButtonFilters?: boolean;
    isFilterLoading?: boolean;
}

export function CampaignFilter({
    filterParams,
    setFilterParams,
    isCardView,
    setIsCardView,
    searchKey = 'name',
    isDeclinedStatus = false,
    showCampaignStatus = true,
    showLongButton,
    showButtonFilters,
    isFilterLoading = false,
}: ICampaignFilterProps) {
    const { t } = useTranslation();
    const { OfferActiveStatusEnum, OfferNegativeStatusEnum } = useSelector((state: IAllStates) => state.enums);
    const [isContracts, setIsContracts] = useState(false);

    const changeParamsHandler = (paramKey: string, paramValue: string | string[] | number | boolean) => {
        setFilterParams(prev => ({ ...prev, [paramKey]: paramValue !== 'null' ? paramValue : '', page: 1 }));
    };

    const handleSearch = (value: string, name: string) => {
        setFilterParams(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    const handleSortBy = (by: 'date' | 'publishFrom' | 'publishTo' | 'nbPosts' | '') => {
        setFilterParams(prev => ({ ...prev, sortBy: by }));
    };

    const handleSortType = (type: 'ASC' | 'DESC') => {
        setFilterParams(prev => ({ ...prev, sortType: type }));
    };

    return (
        <Row className="justify-content-between">
            <Col>
                <Row className="align-items-center">
                    {showButtonFilters && (
                        <Col>
                            <Button
                                className={`${isContracts ? 'btn-outline-primary' : ''} mr-4`}
                                disabled={isFilterLoading}
                                onClick={() => {
                                    setIsContracts(false);
                                    changeParamsHandler('isContracts', false);
                                }}
                            >
                                Offers
                            </Button>
                            {showLongButton && (
                                <Button
                                    className={isContracts ? '' : 'btn-outline-primary'}
                                    disabled={isFilterLoading}
                                    onClick={() => {
                                        setIsContracts(true);
                                        changeParamsHandler('isContracts', true);
                                    }}
                                >
                                    Long term contracts
                                </Button>
                            )}
                        </Col>
                    )}

                    <Col md={4} className="my-2 my-md-0">
                        <SearchInput
                            name={searchKey}
                            defaultValue={(filterParams as Record<string, any>)?.[searchKey] ?? ''}
                            onSearch={handleSearch}
                            isLoading={isFilterLoading}
                            label={t('general.button.search')}
                        />
                    </Col>
                    {showCampaignStatus && (
                        <Col md={3}>
                            <CustomAutocomplete
                                dataArray={isDeclinedStatus ? OfferNegativeStatusEnum : OfferActiveStatusEnum}
                                isWithImage={false}
                                inputLabel="campaign_status"
                                isMultiple={false}
                                disableClearable={false}
                                photoLabel=""
                                queryFilterKey="status"
                                inputLabelTranslation={t('influencer.option.offer_status')}
                                handleChangeParams={changeParamsHandler}
                                isDisabled={isFilterLoading}
                            />
                        </Col>
                    )}
                </Row>
            </Col>
            <Col className="col-md-auto col-12">
                <div className="d-flex align-items-center justify-content-end">
                    {filterParams?.sortType === 'ASC' ? (
                        <BsSortDown
                            style={{ width: 16, height: 16 }}
                            className="cursor-pointer"
                            onClick={() => handleSortType('DESC')}
                        />
                    ) : (
                        <BsSortUp
                            style={{ width: 16, height: 16 }}
                            className="cursor-pointer"
                            onClick={() => handleSortType('ASC')}
                        />
                    )}
                    <DropdownButton
                        className="mr-2 pl-1 font-weight-normal"
                        variant="transparent"
                        title={`${
                            filterParams?.sortBy
                                ? t(`influencer.explore.${filterParams?.sortBy}`)
                                : t('general.button.sort_by')
                        }`}
                    >
                        {filterParams?.sortBy && <Dropdown.Item onClick={() => handleSortBy('')}>Clear</Dropdown.Item>}
                        <Dropdown.Item
                            className={`text-${filterParams?.sortBy === 'date' ? 'primary' : ''}`}
                            onClick={() => handleSortBy('date')}
                        >
                            {t('influencer.explore.date')}
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => handleSortBy('nbPosts')}
                            className={`text-${filterParams?.sortBy === 'nbPosts' ? 'primary' : ''}`}
                        >
                            {t('influencer.explore.nbPosts')}
                        </Dropdown.Item>
                    </DropdownButton>
                    {setIsCardView && (
                        <>
                            <Button
                                onClick={() => setIsCardView(false)}
                                variant="icon"
                                className={isCardView ? '' : 'active'}
                            >
                                <FaThList />
                            </Button>
                            <Button
                                onClick={() => setIsCardView(true)}
                                variant="icon"
                                className={`ml-2 ${isCardView ? 'active' : ''}`}
                            >
                                <FaTh />
                            </Button>
                        </>
                    )}
                </div>
            </Col>
        </Row>
    );
}
